'use client';

import type { IconButtonProps } from '@mui/material/IconButton';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { m } from 'framer-motion';
import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import SvgIcon from '@mui/material/SvgIcon';
import Tab from '@mui/material/Tab';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { GQLMutation, GQLQuery } from '@/lib/client';
import { useBoolean } from '@/hooks/use-boolean';
import { Iconify } from '@/components/iconify';
import { Scrollbar } from '@/components/scrollbar';
import { CustomTabs } from '@/components/custom-tabs';
import { varHover } from '@/components/animate/variant/actions';
import { toastSuccess } from '@/lib/toast';
import { Q_MY_NOTIFICATIONS } from '@/lib/queries/notification.query';
import {
  M_ARCHIVE_NOTIFICATION,
  M_MARK_ALL_NOTIFICATIONS_READ,
  M_MARK_NOTIFICATION_READ,
} from '@/lib/mutations/notification.mutation';
import type { AgentNotification, NotificationPage } from '@/sections/notifications/types';
import { NotificationItem } from './notification-item';

type InboxTab = 'ALL' | 'UNREAD' | 'ARCHIVED';

export type NotificationsDrawerProps = IconButtonProps & { enabled?: boolean };

export function NotificationsDrawer({ enabled = true, sx, ...other }: NotificationsDrawerProps) {
  const drawer = useBoolean();
  const router = useRouter();
  const [currentTab, setCurrentTab] = useState<InboxTab>('ALL');
  const newestSeen = useRef<string | null>(null);

  const { data, loading, refetch } = GQLQuery({
    query: Q_MY_NOTIFICATIONS,
    queryAction: 'myNotifications',
    variables: { input: { tab: currentTab, page: 0, pageSize: 30 } },
    skip: !enabled,
    pollInterval: 30_000,
    fetchPolicy: 'cache-and-network',
  });
  const page = (data ?? { count: 0, unreadCount: 0, rows: [] }) as NotificationPage;

  const markRead = GQLMutation({ mutation: M_MARK_NOTIFICATION_READ, resolver: 'markNotificationRead', silentError: true });
  const markAll = GQLMutation({ mutation: M_MARK_ALL_NOTIFICATIONS_READ, resolver: 'markAllNotificationsRead', silentError: true });
  const archive = GQLMutation({ mutation: M_ARCHIVE_NOTIFICATION, resolver: 'archiveNotification', silentError: true });

  useEffect(() => {
    if (!enabled) return undefined;
    const handleFocus = () => void refetch();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [enabled, refetch]);

  useEffect(() => {
    if (currentTab !== 'ALL') return;
    const newest = page.rows[0];
    if (!newest) return;
    if (newestSeen.current && newestSeen.current !== newest.id && newest.isUnread) {
      toastSuccess(newest.title);
    }
    newestSeen.current = newest.id;
  }, [currentTab, page.rows]);

  const tabs = useMemo(
    () => [
      { value: 'ALL' as const, label: 'All' },
      { value: 'UNREAD' as const, label: `Unread (${page.unreadCount})` },
      { value: 'ARCHIVED' as const, label: 'Archived' },
    ],
    [page.unreadCount],
  );

  const openNotification = useCallback(
    async (notification: AgentNotification) => {
      if (notification.isUnread) {
        await markRead.action({ variables: { input: { id: notification.id } } });
        await refetch();
      }
      drawer.onFalse();
      if (notification.actionType === 'RUN_DETAIL' && notification.actionRunId) {
        router.push(`/agent/janta?runId=${encodeURIComponent(notification.actionRunId)}`);
      } else if (notification.actionType === 'AGENT_HOME') {
        router.push('/agent');
      }
    },
    [drawer, markRead, refetch, router],
  );

  if (!enabled) return null;

  return (
    <>
      <IconButton
        component={m.button}
        whileTap="tap"
        whileHover="hover"
        variants={varHover(1.05)}
        onClick={() => {
          drawer.onTrue();
          void refetch();
        }}
        sx={sx}
        {...other}
      >
        <Badge badgeContent={page.unreadCount} color="error" max={99}>
          <SvgIcon>
            <path fill="currentColor" d="M18.75 9v.704c0 .845.24 1.671.692 2.374l1.108 1.723c1.011 1.574.239 3.713-1.52 4.21a25.794 25.794 0 0 1-14.06 0c-1.759-.497-2.531-2.636-1.52-4.21l1.108-1.723a4.393 4.393 0 0 0 .693-2.374V9c0-3.866 3.022-7 6.749-7s6.75 3.134 6.75 7" opacity="0.5" />
            <path fill="currentColor" d="M12.75 6a.75.75 0 0 0-1.5 0v4a.75.75 0 0 0 1.5 0zM7.243 18.545a5.002 5.002 0 0 0 9.513 0c-3.145.59-6.367.59-9.513 0" />
          </SvgIcon>
        </Badge>
      </IconButton>

      <Drawer open={drawer.value} onClose={drawer.onFalse} anchor="right" slotProps={{ backdrop: { invisible: true } }} PaperProps={{ sx: { width: 1, maxWidth: 420 } }}>
        <Stack direction="row" alignItems="center" sx={{ py: 2, pl: 2.5, pr: 1, minHeight: 68 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>Notifications</Typography>
          {!!page.unreadCount && (
            <Tooltip title="Mark all as read">
              <IconButton color="primary" onClick={async () => { await markAll.action(); await refetch(); }}>
                <Iconify icon="eva:done-all-fill" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={drawer.onFalse} sx={{ display: { xs: 'inline-flex', sm: 'none' } }}>
            <Iconify icon="mingcute:close-line" />
          </IconButton>
        </Stack>

        <CustomTabs variant="fullWidth" value={currentTab} onChange={(_, value: InboxTab) => setCurrentTab(value)}>
          {tabs.map((tab) => <Tab key={tab.value} value={tab.value} label={tab.label} />)}
        </CustomTabs>

        <Scrollbar>
          <Box component="ul" sx={{ p: 0, m: 0 }}>
            {loading && !page.rows.length && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress size={24} /></Stack>}
            {!loading && !page.rows.length && (
              <Stack alignItems="center" spacing={1} sx={{ py: 8, px: 3 }}>
                <Iconify icon="solar:bell-off-linear" width={36} />
                <Typography variant="body2" color="text.secondary">No notifications here.</Typography>
              </Stack>
            )}
            {page.rows.map((notification) => (
              <Box component="li" key={notification.id} sx={{ display: 'flex' }}>
                <NotificationItem
                  notification={notification}
                  onOpen={() => void openNotification(notification)}
                  onArchive={async () => { await archive.action({ variables: { input: { id: notification.id } } }); await refetch(); }}
                />
              </Box>
            ))}
          </Box>
        </Scrollbar>
        <Box sx={{ p: 1 }}>
          <Button fullWidth size="large" onClick={() => { drawer.onFalse(); router.push('/agent/notifications'); }}>
            View all
          </Button>
        </Box>
      </Drawer>
    </>
  );
}
